/**
 * Postular a mano, sin plan: «Vacantes para ti» y la hoja de postular.
 *
 * Los tres endpoints existen SÓLO con `JOB_APPLY_FREE_TIER=on` (si no, 404
 * `not_enabled`), y ninguno llama al modelo: la lista es la búsqueda por
 * embedding con el mismo filtro del envío, y la plantilla se arma con campos.
 *
 * Estados y no `throw`, como el resto de los clientes: cada código del servidor
 * se dice distinto en la hoja (un 429 no es un error, es el cupo del día).
 */
import { apiFetch } from '~/lib/api';

export interface ManualQuota {
  used: number;
  limit: number;
  /** Con plan el cupo es el del plan, compartido con el envío automático. */
  plan_active: boolean;
}

/** Una fila de `GET /api/jobs-for-you`. El salario se lee con `formatJobWage`. */
export interface JobForYou {
  _id: string;
  title: string;
  company: string;
  location?: string | null;
  country?: string | null;
  /** El `feed_type` de la vacante: h2b, h2a_jo, dol_h2a, jobbank, eures_open… */
  visa_type?: string | null;
  start_date?: string | null;
  wage_from?: number;
  wage_to?: number;
  wage_per?: string;
  wage_currency?: string;
  contact_email_present: boolean;
  /** El empleador ya contestó «no hay cupo» (ADR-046): sin botón. */
  closed: boolean;
}

/**
 * Sin plan, «Vacantes para ti» llega bloqueada: de cada recomendada solo lo que
 * no la identifica. Empresa, cargo y lugar no salen del servidor (2026-10-30).
 */
export interface LockedJobPreview {
  visa_type?: string | null;
  start_date?: string | null;
  wage_from?: number;
  wage_to?: number;
  wage_per?: string;
  wage_currency?: string;
}

/** Lo que el envío del plan mandó hoy, con la vacante a la que fue. */
export type AppliedTodayJob = JobForYou & { sent_at: string };

export type JobsForYouState =
  | {
      status: 'ok';
      jobs: JobForYou[];
      quota: ManualQuota;
      /** Sin plan: `jobs` vacío y la vista previa borrosa en `preview`. */
      locked: boolean;
      preview: LockedJobPreview[];
      appliedToday: AppliedTodayJob[];
    }
  | { status: 'error' };

export async function fetchJobsForYou(): Promise<JobsForYouState> {
  try {
    const res = await apiFetch('/job-apply/api/jobs-for-you');
    if (!res.ok) return { status: 'error' };
    const body = (await res.json().catch(() => null)) as Record<string, any> | null;
    if (!body || !Array.isArray(body.jobs)) return { status: 'error' };
    return {
      status: 'ok',
      jobs: body.jobs as JobForYou[],
      quota: readQuota(body.quota),
      locked: body.locked === true,
      preview: Array.isArray(body.preview) ? (body.preview as LockedJobPreview[]) : [],
      appliedToday: Array.isArray(body.applied_today) ? (body.applied_today as AppliedTodayJob[]) : [],
    };
  } catch {
    return { status: 'error' };
  }
}

/** Una fila del buscador: la vacante completa, con o sin plan, y si ya postuló. */
export type SearchJob = JobForYou & { applied: boolean };

export type JobSearchState =
  | { status: 'ok'; jobs: SearchJob[]; hasMore: boolean; page: number; quota: ManualQuota }
  | { status: 'error' };

export type SearchSort = 'mix' | 'newest' | 'pay' | 'soon';
export type SearchProgram = 'h2b' | 'h2a' | 'jobbank' | 'eures';
export type SearchStart = 'month' | 'quarter' | 'later';

/** Los filtros del buscador; vacío = todo el feed de la persona. */
export interface SearchFilters {
  program?: SearchProgram;
  /** `US:TX`, `CA:ON`, `EU:ES`. */
  region?: string;
  minWage?: number;
  start?: SearchStart;
  housing?: boolean;
  /** Sin texto: `mix` por defecto. Con texto manda la relevancia. */
  sort?: SearchSort;
}

/**
 * El buscador gratis (`GET /api/jobs/search`): con texto busca por sentido; sin
 * texto, una mezcla (o el orden elegido). Los filtros solo acotan.
 */
export async function searchJobs(q: string, page = 0, filters: SearchFilters = {}): Promise<JobSearchState> {
  const params = new URLSearchParams();
  if (q.trim()) params.set('q', q.trim());
  if (page > 0) params.set('page', String(page));
  if (filters.program) params.set('program', filters.program);
  if (filters.region) params.set('region', filters.region);
  if (filters.minWage) params.set('min_wage', String(filters.minWage));
  if (filters.start) params.set('start', filters.start);
  if (filters.housing) params.set('housing', '1');
  if (filters.sort && filters.sort !== 'mix') params.set('sort', filters.sort);
  const qs = params.toString();
  try {
    const res = await apiFetch(`/job-apply/api/jobs/search${qs ? `?${qs}` : ''}`);
    if (!res.ok) return { status: 'error' };
    const body = (await res.json().catch(() => null)) as Record<string, any> | null;
    if (!body || !Array.isArray(body.jobs)) return { status: 'error' };
    return {
      status: 'ok',
      jobs: body.jobs as SearchJob[],
      hasMore: body.has_more === true,
      page: Number(body.page) || 0,
      quota: readQuota(body.quota),
    };
  } catch {
    return { status: 'error' };
  }
}

/** Qué hay en el feed de la persona, para no ofrecer filtros vacíos. */
export interface SearchFacets {
  total: number;
  programs: { key: SearchProgram; count: number }[];
  regions: { key: string; count: number }[];
  housing: number;
}

export async function fetchSearchFacets(): Promise<SearchFacets | null> {
  try {
    const res = await apiFetch('/job-apply/api/jobs/search/facets');
    if (!res.ok) return null;
    const body = (await res.json().catch(() => null)) as Record<string, any> | null;
    if (!body || !Array.isArray(body.programs) || !Array.isArray(body.regions)) return null;
    return {
      total: Number(body.total) || 0,
      programs: body.programs as SearchFacets['programs'],
      regions: body.regions as SearchFacets['regions'],
      housing: Number(body.housing) || 0,
    };
  } catch {
    return null;
  }
}

/** Una vacante guardada: la del buscador, con cuándo se guardó. */
export type SavedJob = SearchJob & { saved_at?: string };

/** Las guardadas (`GET /api/jobs/saved`), de la más nueva a la más vieja. */
export async function fetchSavedJobs(): Promise<{ status: 'ok'; jobs: SavedJob[] } | { status: 'error' }> {
  try {
    const res = await apiFetch('/job-apply/api/jobs/saved');
    if (!res.ok) return { status: 'error' };
    const body = (await res.json().catch(() => null)) as Record<string, any> | null;
    if (!body || !Array.isArray(body.jobs)) return { status: 'error' };
    return { status: 'ok', jobs: body.jobs as SavedJob[] };
  } catch {
    return { status: 'error' };
  }
}

/** Guardar o quitar una vacante. `ok:false` deja la estrella como estaba. */
export async function setJobSaved(jobId: string, saved: boolean): Promise<{ ok: boolean; tooMany?: boolean }> {
  try {
    const res = await apiFetch(`/job-apply/api/jobs/saved/${encodeURIComponent(jobId)}`, {
      method: saved ? 'PUT' : 'DELETE',
    });
    if (res.ok) return { ok: true };
    return { ok: false, tooMany: res.status === 409 };
  } catch {
    return { ok: false };
  }
}

function readQuota(raw: unknown): ManualQuota {
  const q = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const used = Number(q.used);
  const limit = Number(q.limit);
  return {
    used: Number.isFinite(used) && used > 0 ? used : 0,
    limit: Number.isFinite(limit) && limit > 0 ? limit : 10,
    plan_active: q.plan_active === true,
  };
}

/** Los campos con que «La carta del plan» se describe, sin escribir ninguna carta. */
export interface PlanLetterHints {
  company: string | null;
  start_date: string | null;
  years: number | null;
}

export type PreviewState =
  | { status: 'ok'; to: string; subject: string; body: string; hints: PlanLetterHints }
  | { status: 'job_closed' }
  | { status: 'job_not_active' }
  | { status: 'no_contact_email' }
  | { status: 'error' };

export async function fetchManualPreview(jobId: string): Promise<PreviewState> {
  try {
    const res = await apiFetch(
      `/job-apply/api/apply-manual/preview?job_posting_id=${encodeURIComponent(jobId)}`,
    );
    const body = (await res.json().catch(() => ({}))) as Record<string, any>;
    if (!res.ok) {
      // Los mismos 409 del envío: cerrada por el empleador o fuera del feed.
      if (body.error === 'job_closed') return { status: 'job_closed' };
      if (body.error === 'job_not_active') return { status: 'job_not_active' };
      if (body.error === 'no_contact_email') return { status: 'no_contact_email' };
      return { status: 'error' };
    }
    const hints = (body.plan_letter_hints ?? {}) as Record<string, unknown>;
    return {
      status: 'ok',
      to: String(body.to ?? ''),
      subject: String(body.subject ?? ''),
      body: String(body.body ?? ''),
      hints: {
        company: typeof hints.company === 'string' && hints.company ? hints.company : null,
        start_date: typeof hints.start_date === 'string' && hints.start_date ? hints.start_date : null,
        years: typeof hints.years === 'number' && hints.years > 0 ? hints.years : null,
      },
    };
  } catch {
    return { status: 'error' };
  }
}

export type ManualMode = 'gmail' | 'copy';

export type ApplyManualResult =
  | { kind: 'sent' }
  | { kind: 'copied'; to: string; subject: string; body: string }
  | { kind: 'daily_limit'; quota: ManualQuota }
  | { kind: 'already_applied' }
  | { kind: 'job_closed' }
  | { kind: 'job_not_active' }
  | { kind: 'no_contact_email' }
  | { kind: 'gmail_reconnect' }
  /** Sin la versión vigente de los términos: hay que abrir el paso de consentimiento. */
  | { kind: 'terms_required' }
  /** La membresía está en pausa (rebotes): no sale nada, tampoco a mano. */
  | { kind: 'sending_paused' }
  | { kind: 'cv_required' }
  | { kind: 'failed' };

export async function applyManual(jobId: string, mode: ManualMode): Promise<ApplyManualResult> {
  let res: Response;
  try {
    res = await apiFetch('/job-apply/api/apply-manual', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ job_posting_id: jobId, mode }),
    });
  } catch {
    return { kind: 'failed' };
  }
  const body = (await res.json().catch(() => ({}))) as Record<string, any>;
  if (res.ok) {
    if (mode === 'copy') {
      return {
        kind: 'copied',
        to: String(body.to ?? ''),
        subject: String(body.subject ?? ''),
        body: String(body.body ?? ''),
      };
    }
    // `warning: 'record_failed'` también salió: el correo ya está en camino.
    return { kind: 'sent' };
  }
  switch (body.error) {
    case 'daily_limit_reached':
      return { kind: 'daily_limit', quota: readQuota(body.quota) };
    case 'already_applied':
      return { kind: 'already_applied' };
    case 'job_closed':
      return { kind: 'job_closed' };
    case 'job_not_active':
      return { kind: 'job_not_active' };
    case 'no_contact_email':
      return { kind: 'no_contact_email' };
    case 'terms_required':
      return { kind: 'terms_required' };
    case 'sending_paused':
      return { kind: 'sending_paused' };
    // Sin Gmail conectado o con el permiso vencido: el mismo camino, reconectar.
    case 'gmail_required':
    case 'gmail_reconnect_required':
      return { kind: 'gmail_reconnect' };
    case 'cv_required':
      return { kind: 'cv_required' };
    default:
      return { kind: 'failed' };
  }
}
